/**
 * useRecentDocuments Hook
 *
 * Fetches recently accessed/modified documents for the authenticated user
 * in a managed tenant via delegated permissions (OBO flow).
 *
 * Uses an in-memory cache to prevent refetching when navigating between pages.
 */

import { useState, useEffect, useCallback } from 'react';
import { TenantApiService } from '../services/tenantApi';
import type { SharePointDocument } from '../types/sharepoint';

interface UseRecentDocumentsOptions {
  tenantId: string;
  limit?: number;
  enabled?: boolean;
}

interface UseRecentDocumentsResult {
  documents: SharePointDocument[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface CacheEntry {
  documents: SharePointDocument[];
  timestamp: number;
}

// Module-level cache to persist across component mounts
const documentsCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes (shorter for recent activity)

/**
 * Hook to fetch the user's recently accessed/modified documents in a managed tenant.
 *
 * @param options.tenantId - The managed tenant ID to query for documents
 * @param options.limit - Maximum number of documents to return (default 10, max 50)
 * @param options.enabled - Whether to fetch documents (default: true)
 * @returns Object with documents, loading state, error, and refetch function
 */
export function useRecentDocuments({
  tenantId,
  limit,
  enabled = true,
}: UseRecentDocumentsOptions): UseRecentDocumentsResult {
  // Create cache key that includes limit
  const cacheKey = `${tenantId}:${limit ?? 'default'}`;
  const cachedEntry = tenantId ? documentsCache.get(cacheKey) : undefined;
  const isCacheValid = cachedEntry && (Date.now() - cachedEntry.timestamp) < CACHE_TTL_MS;

  const [documents, setDocuments] = useState<SharePointDocument[]>(isCacheValid ? cachedEntry.documents : []);
  const [isLoading, setIsLoading] = useState<boolean>(!isCacheValid && enabled && !!tenantId);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async (skipCache = false) => {
    // Don't fetch if disabled or no tenantId
    if (!enabled || !tenantId) {
      setDocuments([]);
      setIsLoading(false);
      return;
    }

    // Check cache first (unless skipCache is true for manual refresh)
    if (!skipCache) {
      const cached = documentsCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
        setDocuments(cached.documents);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedDocuments = await TenantApiService.getRecentDocuments(tenantId, limit, skipCache);
      setDocuments(fetchedDocuments);
      // Update cache
      documentsCache.set(cacheKey, { documents: fetchedDocuments, timestamp: Date.now() });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch recent documents';
      setError(errorMessage);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, limit, enabled, cacheKey]);

  // Fetch documents on mount and when dependencies change
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Manual refetch bypasses cache
  const refetch = useCallback(async () => {
    await fetchDocuments(true);
  }, [fetchDocuments]);

  return {
    documents,
    isLoading,
    error,
    refetch,
  };
}
